import React, { useState } from 'react';

import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import QuestionBooleenne, { QuestionBoolChangeEvent } from './QualificationComponents/QuestionBooleenne';

import DropZone from 'components/Shared/DropZone';
import PieceGestionSearch from 'components/Shared/PieceGestionSearch';

import { ReclamationQualificationDetailType } from 'constants.js';
import { ReclamationQualificationDetailCodes } from 'constants.js';

import qualificationHelper from 'utils/qualificationHelper';

import {
    Reclamation,
    QualificationDetail,
    BoolQualificationDetail,
    StringQualificationDetail,
    IntegerQualificationDetail,
    DateQualificationDetail,
    ChoixMultipleQualificationDetail
} from 'types/AddReclamation/reclamation';

// Styles définis comme constantes pour être utilisés avec la propriété sx
const importantTextStyle = {
    textDecoration: 'underline',
    backgroundColor: 'pink'
};


// Type pour les props
type Props = {
    reclamation: Reclamation;
    onChange: (event: any, errors?: any[]) => void;
    onDetailChange: (detail: QualificationDetail | QualificationDetail[] | null, errors?: any[], detailsToDelete?: string[]) => void;
    onSelectPiece: (value: any, errors: any[], isValid?: boolean) => void;
    validationErrors?: any[];
    onNextStepValid: (isValid: boolean) => void;
    namespace: string;
};

// Règles de validation à passer en paramètre de qualificationHelper.getErrors
// La fonction d'assertion renvoie true quand la règle est respectée
const _rules = [
    {
        key: ReclamationQualificationDetailCodes.NomBLEqualNomClient,
        errorMsg: "Le nom sur l'étiquette du colis est-il le nom du client ?",
        fields: ReclamationQualificationDetailCodes.NomBLEqualNomClient,
        assertion: function (a: any) {
            return a !== undefined && a !== null && a !== '';
        }
    },
    {
        key: ReclamationQualificationDetailCodes.NomEtiquette,
        errorMsg: "Le nom sur l'étiquette est obligatoire.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.NomEtiquette],
        assertion: function (a: any[]) {
            if (a[0] === 'false') {
                return a[1] !== undefined && a[1] !== null && a[1] !== '';
            }
            return true;
        }
    },
    {
        key: ReclamationQualificationDetailCodes.PhotoJoignable,
        errorMsg: "Veuillez indiquer si vous pouvez joindre une photo du colis.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.PhotoJoignable],
        assertion: function (a: any[]) {
            if (a[0] === 'false') {
                return a[1] !== undefined && a[1] !== null && a[1] !== '';
            }
            return true;
        }
    },
    {
        key: 'DetailSurColisError',
        errorMsg: "Veuillez indiquer au moins un numéro de colis ou de pièce présent sur le colis.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.NumeroColisVisible, ReclamationQualificationDetailCodes.NumeroColis, ReclamationQualificationDetailCodes.NumeroPieceSurColis],
        assertion: function (a: any[]) {
            if (a[0] === 'true' && a[1] === 'true') {
                return (a[2] !== undefined && a[2] !== null && a[2] !== '') ||
                    (a[3] !== undefined && a[3] !== null && a[3] !== '');
            }
            return true;
        }
    },
    {
        key: 'DetailColisError',
        errorMsg: "Veuillez indiquer au moins une des trois valeurs.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.NumeroColisVisible, ReclamationQualificationDetailCodes.NomBonLivraison, ReclamationQualificationDetailCodes.NumeroPieceColis, ReclamationQualificationDetailCodes.CodeArticle],
        assertion: function (a: any[]) {
            if (a[0] === 'true' && a[1] === 'false') {
                return (a[2] !== undefined && a[2] !== null && a[2] !== '') ||
                    (a[3] !== undefined && a[3] !== null && a[3] !== '') ||
                    (a[4] !== undefined && a[4] !== null && a[4] !== '');
            }
            return true;
        }
    },
    {
        key: ReclamationQualificationDetailCodes.DateLivraison,
        errorMsg: "Veuillez indiquer la date de livraison du colis.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.DateLivraison],
        assertion: function (a: any[]) {
            if (a[0] !== '') {
                return a[1] !== undefined && a[1] !== null && a[1] !== '';
            }
            return true;
        }
    },
    {
        key: ReclamationQualificationDetailCodes.AvecLivraison,
        errorMsg: "Veuillez indiquer avec quelle livraison vous avez reçu le colis.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.AvecLivraison, 'numeroPiece'],
        assertion: function (a: any[]) {
            if (a[0] !== '') {
                if (a[1] === '') {
                    return false;
                }
                if (a[1] === 0 || a[1] === 1) {
                    return a[2] !== undefined && a[2] !== null && a[2] !== '';
                }
                return true;
            }
            return true;
        }
    },
    {
        key: ReclamationQualificationDetailCodes.ManqueMarchandise,
        errorMsg: "Préciser s'il manque de la marchandise.",
        fields: [ReclamationQualificationDetailCodes.NomBLEqualNomClient, ReclamationQualificationDetailCodes.ManqueMarchandise],
        assertion: function (a: any[]) {
            if (a[0] !== '') {
                return a[1] !== undefined && a[1] !== null && a[1] !== '';
            }
            return true;
        }
    }
];

function ColisErreurDestinataireStep({ reclamation, onChange, onDetailChange, onSelectPiece, onNextStepValid, namespace }: Props) {

    // State
    const [dialogueImageEtiquette, setDialogueImageEtiquette] = useState<boolean>(false);

    // Effet pour initialiser la validation
    React.useEffect(() => {
        onNextStepValid(false);
        onDetailChange(null, [{ key: ReclamationQualificationDetailCodes.NomBLEqualNomClient, errorMsg: "Le nom sur l'étiquette du colis est-il le nom du client ?" }], [] )
    }, []);


    // Cette qualification n'a pas besoin de pièce, si il y en a une chargé pour l'onglet information, on l'efface
    React.useEffect(() => {
        if(reclamation.numeroPiece)
        {
            onSelectPiece("", [{ key: ReclamationQualificationDetailCodes.NomBLEqualNomClient, errorMsg: "Le nom sur l'étiquette du colis est-il le nom du client ?" }], false);
        }
    }, [reclamation.numeroPiece]);

    // Fonctions
    const getDetailsColisToDelete = (): string[] => {
        const numeroColisVisible = getNomColisVisible();
        
        if (numeroColisVisible === 'true') {
            return [ReclamationQualificationDetailCodes.NomBonLivraison, ReclamationQualificationDetailCodes.NumeroPieceColis, ReclamationQualificationDetailCodes.CodeArticle];
        }
        else {
            return [ReclamationQualificationDetailCodes.NumeroColis, ReclamationQualificationDetailCodes.NumeroPieceSurColis];
        }
    };

    // Obtention d'un détail quelconque.
    const getDetail = (code: string, typeValeur: 'valeurString' | 'valeurBool' | 'valeurDate' | 'valeurInteger'): string | number | null | undefined => {
        // Initialisation de la valeur
        let valeur: string | number | null | undefined = '';

        // Obtention du code
        const detail = reclamation.qualificationDetails.find(detail => detail.code === code);

        // Affectation si possible de la valeur en fonction du type demandé
        if (detail !== undefined) {
            switch (typeValeur) {
                case 'valeurString':
                    valeur = (detail as StringQualificationDetail).valeurString;
                    break;
                case 'valeurBool':
                    valeur = (detail as BoolQualificationDetail).valeurBool;
                    break;
                case 'valeurDate':
                    valeur = (detail as DateQualificationDetail).valeurDate;
                    break;
                case 'valeurInteger':
                    valeur = (detail as IntegerQualificationDetail).valeurInteger;
                    break;
            }
        }

        return valeur;
    };
    
    // Fonctions d'accès aux détails de qualification
    const getNomBLEqualNomClient = (): '' | "false" | "true" => {
        return getDetail(ReclamationQualificationDetailCodes.NomBLEqualNomClient, "valeurBool") as ('' | "false" | "true") || '';
    };
    
    const getNomColisVisible = (): '' | "false" | "true" => {
        return getDetail(ReclamationQualificationDetailCodes.NumeroColisVisible, 'valeurBool') as ('' | "false" | "true") || 'true';
    };
    
    const getNomEtiquette = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NomEtiquette, 'valeurString') as string || '';
    };
    
    const getManqueMarchandise = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.ManqueMarchandise, 'valeurBool') as string || '';
    };
    
    const getPhotoJoignable = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.PhotoJoignable, 'valeurBool') as string || '';
    };
    
    const getNumeroPieceEtiquette = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NumeroPieceEtiquette, 'valeurString') as string || '';
    };
    
    const getCodeClientEtiquette = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.CodeClientEtiquette, 'valeurString') as string || '';
    };
    
    const getAdresseLivraisonEtiquette = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.AdresseLivraisonEtiquette, 'valeurString') as string || '';
    };
    
    const getNomBonLivraison = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NomBonLivraison, 'valeurString') as string || '';
    };
    
    const getNumeroPieceColis = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NumeroPieceColis, 'valeurString') as string || '';
    };
    
    const getCodeArticle = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.CodeArticle, 'valeurString') as string || '';
    };
    
    const getDateLivraison = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.DateLivraison, 'valeurDate') as string || '';
    };
    
    const getAvecLivraison = (): '' | number => {
        return getDetail(ReclamationQualificationDetailCodes.AvecLivraison, 'valeurInteger') as ('' | number) || '';
    };
    
    const getNumeroPiece = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.AvecLivraison, 'valeurString') as string || '';
    };
    
    const getNumeroColis = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NumeroColis, 'valeurString') as string || '';
    };
    
    const getNumeroPieceSurColis = (): string => {
        return getDetail(ReclamationQualificationDetailCodes.NumeroPieceSurColis, 'valeurString') as string || '';
    };


    // Génère un tableau des champs à contrôler
    const getToCheck = (champ?: string, valeur?: any): Record<string, any> => {
        const result: Record<string, any> = {
            [ReclamationQualificationDetailCodes.NomBLEqualNomClient]: getNomBLEqualNomClient(),
            [ReclamationQualificationDetailCodes.NumeroColisVisible]: getNomColisVisible(),
            [ReclamationQualificationDetailCodes.NomEtiquette]: getNomEtiquette(),
            [ReclamationQualificationDetailCodes.CodeClientEtiquette]: getCodeClientEtiquette(),
            [ReclamationQualificationDetailCodes.AdresseLivraisonEtiquette]: getAdresseLivraisonEtiquette(),
            [ReclamationQualificationDetailCodes.ManqueMarchandise]: getManqueMarchandise(),
            [ReclamationQualificationDetailCodes.PhotoJoignable]: getPhotoJoignable(),
            [ReclamationQualificationDetailCodes.DateLivraison]: getDateLivraison(),
            [ReclamationQualificationDetailCodes.AvecLivraison]: getAvecLivraison(),
            numeroPiece: getNumeroPiece(),
            [ReclamationQualificationDetailCodes.NomBonLivraison]: getNomBonLivraison(),
            [ReclamationQualificationDetailCodes.NumeroPieceColis]: getNumeroPieceColis(),
            [ReclamationQualificationDetailCodes.CodeArticle]: getCodeArticle(),
            [ReclamationQualificationDetailCodes.NumeroColis]: getNumeroColis(),
            [ReclamationQualificationDetailCodes.NumeroPieceSurColis]: getNumeroPieceSurColis()
        };

        if (champ) {
            result[champ] = valeur;
        }
        return result;
    };

    const handleDetailNomBLEqualNomClientChange = (changeEvent: QuestionBoolChangeEvent): void => {

        const qualificationDetails: BoolQualificationDetail[] = [{
            position: changeEvent.position,
            code: changeEvent.code,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: changeEvent.legende,
            valeurBool: changeEvent.value as ("" | "true" | "false"),
            valeurString: changeEvent.libelle
        }];

        if(changeEvent.value === "true")
        {
            qualificationDetails.push({                
                position: changeEvent.position+1,
                code: ReclamationQualificationDetailCodes.NumeroColisVisible,
                type: ReclamationQualificationDetailType.Booleen,
                libelle: "Pouvez-vous indiquer le numéro de colis ?",
                valeurBool: "true",
                valeurString: "Oui"
            });
        }

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NomBLEqualNomClient, changeEvent.value);
        
        if(changeEvent.value === "true")
        {
            toCheckValue[ReclamationQualificationDetailCodes.NumeroColisVisible] = "true";
        }
        else
        {            
            toCheckValue[ReclamationQualificationDetailCodes.NumeroColisVisible] = "";
        }

        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        let detailsToDelete: string[] = [];

        if (changeEvent.value === "true") {
            detailsToDelete = [ReclamationQualificationDetailCodes.NomEtiquette, ReclamationQualificationDetailCodes.NumeroPieceEtiquette, ReclamationQualificationDetailCodes.PhotoJoignable];
        }
        else {
            detailsToDelete = [ReclamationQualificationDetailCodes.NumeroColisVisible, ReclamationQualificationDetailCodes.NomBonLivraison, ReclamationQualificationDetailCodes.NumeroPieceColis, ReclamationQualificationDetailCodes.CodeArticle,
            ReclamationQualificationDetailCodes.NumeroColis, ReclamationQualificationDetailCodes.NumeroPieceSurColis];
        }

        onDetailChange(qualificationDetails, errors, detailsToDelete);
    };

    const handleNumeroColisVisibleChange = (changeEvent: QuestionBoolChangeEvent): void => {
        const qualificationDetail: BoolQualificationDetail = {
            position: changeEvent.position,
            code: changeEvent.code,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: changeEvent.legende,
            valeurBool: changeEvent.value as ("" | "true" | "false"),
            valeurString: changeEvent.libelle
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NumeroColisVisible, changeEvent.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, getDetailsColisToDelete());
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const toCheckValue = getToCheck();
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onChange(event, errors);
    };

    const handleSelectPiece = (value: any): void => {
        const event = {
            target: { value }
        };

        handleDetailNumeroPieceChange(event);
    };

    const handleDetailNomEtiquetteChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 1,
            code: ReclamationQualificationDetailCodes.NomEtiquette,
            type: ReclamationQualificationDetailType.Texte,
            libelle: 'Nom sur l\'étiquette',
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NomEtiquette, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };


    const handleDetailCodeClientEtiquetteChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.CodeClientEtiquette,
            type: ReclamationQualificationDetailType.Texte,
            libelle: 'Code client sur l\'étiquette',
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.CodeClientEtiquette, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    const handleDetailAdresseLivraisonEtiquetteChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 4,
            code: ReclamationQualificationDetailCodes.AdresseLivraisonEtiquette,
            type: ReclamationQualificationDetailType.Texte,
            libelle: 'Adresse de livraison sur l\'étiquette',
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.AdresseLivraisonEtiquette, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };
    
    const handleDetailNumeroPieceEtiquetteChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 5,
            code: ReclamationQualificationDetailCodes.NumeroPieceEtiquette,
            type: ReclamationQualificationDetailType.Texte,
            libelle: 'Numéro de pièce sur l\'étiquette',
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NumeroPieceEtiquette, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    const handleDetailPhotoJoignableChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: BoolQualificationDetail = {
            position: 6,
            code: ReclamationQualificationDetailCodes.PhotoJoignable,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: "Pouvez-vous joindre la photo de l'étiquette ?",
            valeurBool: event.target.value as ("" | "true" | "false"),
            valeurString: event.target.value === "true" ? "Oui" : event.target.value === "false" ? "Non" : ""
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.PhotoJoignable, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // NomBonLivraison
    const handleDetailNomBonLivraisonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 2,
            code: ReclamationQualificationDetailCodes.NomBonLivraison,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Dans le colis, à quel nom est le bon de livraison?",
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NomBonLivraison, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // NumeroPieceColis
    const handleDetailNumeroPieceColisChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 6,
            code: ReclamationQualificationDetailCodes.NumeroPieceColis,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Pouvez-vous indiquer le numéro de pièce présent dans le colis ?",
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NumeroPieceColis, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // CodeArticle
    const handleDetailCodeArticleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 7,
            code: ReclamationQualificationDetailCodes.CodeArticle,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Code article présent dans la commande initiale",
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.CodeArticle, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // DateLivraison
    const handleDetailDateLivraisonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: DateQualificationDetail = {
            position: 8,
            code: ReclamationQualificationDetailCodes.DateLivraison,
            type: ReclamationQualificationDetailType.Date,
            libelle: "À quelle date avez vous reçu le colis ?",
            valeurDate: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.DateLivraison, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // AvecLivraison
    const handleDetailAvecLivraisonChange = (event: React.ChangeEvent<HTMLInputElement>): void => {

        const qualificationDetail: ChoixMultipleQualificationDetail = {
            position: 9,
            code: ReclamationQualificationDetailCodes.AvecLivraison,
            type: ReclamationQualificationDetailType.ChoixMultiple,
            libelle: 'Avec quelle livraison ?',
            valeurInteger: parseInt(event.target.value),
            valeurString: event.target.value === '2' ? 'Pas de livraison attendue' : getNumeroPiece(),
        };


        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.AvecLivraison, event.target.value);
        // Mettre à jour numeroPiece dans toCheckValue car il dépend de qualificationDetail.valeurString
        toCheckValue['numeroPiece'] = event.target.value === '2' ? '' : getNumeroPiece();
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // NumeroPiece
    const handleDetailNumeroPieceChange = (event: { target: { value: any } }): void => {
        let qualificationDetail = reclamation.qualificationDetails.find(detail => detail.code === ReclamationQualificationDetailCodes.AvecLivraison) as ChoixMultipleQualificationDetail;

        qualificationDetail = { ...qualificationDetail, valeurString: event.target.value };

        const toCheckValue = getToCheck('numeroPiece', event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // ManqueMarchandise
    const handleDetailManqueMarchandiseChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: BoolQualificationDetail = {
            position: 10,
            code: ReclamationQualificationDetailCodes.ManqueMarchandise,
            type: ReclamationQualificationDetailType.Booleen,
            libelle: 'Manque-t-il de la marchandise ?',
            valeurBool: event.target.value as ("" | "true" | "false"),
            valeurString: event.target.value === "true" ? "Oui" : event.target.value === "false" ? "Non" : ""
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.ManqueMarchandise, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, []);
    };

    // NumeroColis
    const handleDetailNumeroColisChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 3,
            code: ReclamationQualificationDetailCodes.NumeroColis,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Numéro de colis commençant par CN ou BA",
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NumeroColis, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, getDetailsColisToDelete());
    };

    // NumeroPieceSurColis
    const handleDetailNumeroPieceSurColisChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const qualificationDetail: StringQualificationDetail = {
            position: 4,
            code: ReclamationQualificationDetailCodes.NumeroPieceSurColis,
            type: ReclamationQualificationDetailType.Texte,
            libelle: "Numéro de pièce sur le colis",
            valeurString: event.target.value
        };

        const toCheckValue = getToCheck(ReclamationQualificationDetailCodes.NumeroPieceSurColis, event.target.value);
        const errors = qualificationHelper.getErrors(toCheckValue, _rules);

        onDetailChange(qualificationDetail, errors, getDetailsColisToDelete());
    };

    // Gestion ouverture / fermeture de la dialogue image étiquette 
    const handleImageEtiquetteDialogState = (open: boolean): void => {
        setDialogueImageEtiquette(open);
    };

    const renderImageEtiquette = (): JSX.Element => {

        return (
            <Dialog
                open={dialogueImageEtiquette}
                maxWidth={'lg'}
                keepMounted
                onClose={() => handleImageEtiquetteDialogState(false)}
                aria-describedby="alert-dialog-show-picture"
            >
                <DialogTitle>
                    <Fab
                        size="small"
                        color="primary"
                        aria-label="close"
                        onClick={() => handleImageEtiquetteDialogState(false)}
                        sx={{
                            position: 'absolute',
                            right: 15,
                            top: 15,
                        }}
                    >
                        <CloseIcon />
                    </Fab>
                </DialogTitle>

                <DialogContent>
                    <Card raised>
                        <CardMedia
                            component="img"
                            src="image-etiquette-colis.png"
                            alt="img"
                        />
                    </Card>
                </DialogContent>
            </Dialog>
        );
    };

    const renderDetailsQualifications = (): JSX.Element => {
        // Initialiser les variables locales pour éviter les appels multiples à getDetail
        const nomBLEqualNomClient = getNomBLEqualNomClient();
        const numeroColisVisible = getNomColisVisible();
        const nomEtiquette = getNomEtiquette();
        const manqueMarchandise = getManqueMarchandise();
        const photoJoignable = getPhotoJoignable();
        const numeroPieceEtiquette = getNumeroPieceEtiquette();
        const codeClientEtiquette = getCodeClientEtiquette();
        const adresseLivraisonEtiquette = getAdresseLivraisonEtiquette();
        const nomBonLivraison = getNomBonLivraison();
        const numeroPieceColis = getNumeroPieceColis();
        const codeArticle = getCodeArticle();
        const dateLivraison = getDateLivraison();
        const avecLivraison = getAvecLivraison();
        const numeroPiece = getNumeroPiece();
        const numeroColis = getNumeroColis();
        const numeroPieceSurColis = getNumeroPieceSurColis();

        // Créer toCheckValue avec les variables locales
        const toCheckValue: Record<string, any> = {
            [ReclamationQualificationDetailCodes.NomBLEqualNomClient]: nomBLEqualNomClient,
            [ReclamationQualificationDetailCodes.NumeroColisVisible]: numeroColisVisible,
            [ReclamationQualificationDetailCodes.NomEtiquette]: nomEtiquette,
            [ReclamationQualificationDetailCodes.CodeClientEtiquette]: codeClientEtiquette,
            [ReclamationQualificationDetailCodes.AdresseLivraisonEtiquette]: adresseLivraisonEtiquette,
            [ReclamationQualificationDetailCodes.ManqueMarchandise]: manqueMarchandise,
            [ReclamationQualificationDetailCodes.PhotoJoignable]: photoJoignable,
            [ReclamationQualificationDetailCodes.DateLivraison]: dateLivraison,
            [ReclamationQualificationDetailCodes.AvecLivraison]: avecLivraison,
            numeroPiece: numeroPiece,
            [ReclamationQualificationDetailCodes.NomBonLivraison]: nomBonLivraison,
            [ReclamationQualificationDetailCodes.NumeroPieceColis]: numeroPieceColis,
            [ReclamationQualificationDetailCodes.CodeArticle]: codeArticle,
            [ReclamationQualificationDetailCodes.NumeroColis]: numeroColis,
            [ReclamationQualificationDetailCodes.NumeroPieceSurColis]: numeroPieceSurColis
        };
        
        let errors = qualificationHelper.getErrors(toCheckValue, _rules);

        errors = errors.filter(x => x.errorMsg);
        errors = errors.filter((_x, index) => index === 0);

        const detailNomEtiquetteError = errors.find(x => x.key === ReclamationQualificationDetailCodes.NomEtiquette);
        const detailPhotoJoignableError = errors.find(x => x.key === ReclamationQualificationDetailCodes.PhotoJoignable);
        const detailDateLivraisonError = errors.find(x => x.key === ReclamationQualificationDetailCodes.DateLivraison);
        const detailAvecLivraisonError = errors.find(x => x.key === ReclamationQualificationDetailCodes.AvecLivraison);
        const detailManqueMarchandiseError = errors.find(x => x.key === ReclamationQualificationDetailCodes.ManqueMarchandise);
        const detailColisError = errors.find(x => x.key === 'DetailColisError');
        const detailSurColisError = errors.find(x => x.key === 'DetailSurColisError');

        return (
            <>
                <QuestionBooleenne
                    legende={"Le nom sur l'étiquette du colis est-il le nom du client ?"}
                    qualificationDetailCode={ReclamationQualificationDetailCodes.NomBLEqualNomClient}
                    value={nomBLEqualNomClient}
                    position={0}
                    onChange={handleDetailNomBLEqualNomClientChange}
                    required
                />

                {nomBLEqualNomClient === 'false' && (
                    <Paper sx={{ mt: 1, p: 1 }}>
                        <Box component="fieldset">
                            <Box component="legend">
                                <Typography variant="subtitle1">Sur le colis (informations sur le colis et non le BL)</Typography>
                            </Box>


                            <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ pl: 1, width: '100%' }}>

                                <FormControl component="fieldset" required>
                                    <FormLabel component="legend" error={detailNomEtiquetteError && detailNomEtiquetteError.errorMsg ? true : false}>Pouvez-vous indiquer le nom présent sur l'<span style={importantTextStyle}>étiquette du colis</span> ?</FormLabel>
                                    <TextField
                                        fullWidth
                                        name="nom-etiquette"
                                        placeholder="Nom présent sur l'étiquette du colis"
                                        onChange={handleDetailNomEtiquetteChange}
                                        value={nomEtiquette}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        sx={{ minWidth: '560px' }}
                                        required
                                        error={detailNomEtiquetteError && detailNomEtiquetteError.errorMsg ? true : false}
                                        helperText={detailNomEtiquetteError ? detailNomEtiquetteError.errorMsg : ''}
                                    />
                                </FormControl>

                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Pouvez-vous indiquer le code client présent sur l'<span style={importantTextStyle}>étiquette du colis</span> ?</FormLabel>
                                    <TextField
                                        fullWidth
                                        name="code-client-etiquette"
                                        placeholder="Code client sur l'étiquette du colis"
                                        onChange={handleDetailCodeClientEtiquetteChange}
                                        value={codeClientEtiquette}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        sx={{ minWidth: '560px' }}
                                    />
                                </FormControl>

                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Pouvez-vous indiquer l'adresse de livraison présente sur l'<span style={importantTextStyle}>étiquette du colis</span> ?</FormLabel>
                                    <TextField
                                        fullWidth
                                        name="adresse-livraison-etiquette"
                                        placeholder="Adresse de livraison sur l'étiquette du colis"
                                        onChange={handleDetailAdresseLivraisonEtiquetteChange}
                                        value={adresseLivraisonEtiquette}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        sx={{ minWidth: '560px' }}
                                    />
                                </FormControl>

                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Pouvez-vous indiquer le numéro de pièce présent sur l'<span style={importantTextStyle}>étiquette du colis</span> ?</FormLabel>
                                    <TextField
                                        fullWidth
                                        name="numero-piece-etiquette"
                                        placeholder="Numéro de pièce sur l'étiquette du colis"
                                        onChange={handleDetailNumeroPieceEtiquetteChange}
                                        value={numeroPieceEtiquette}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        sx={{ minWidth: '560px' }}
                                    />
                                </FormControl>

                                <FormControl component="fieldset" required>
                                    <FormLabel component="legend" error={detailPhotoJoignableError && detailPhotoJoignableError.errorMsg ? true : false}>Pouvez-vous joindre la photo de l'étiquette ?</FormLabel>
                                    <RadioGroup
                                        name="PHOTO_JOIGNABLE"
                                        sx={{ ml: 2 }}
                                        value={photoJoignable}
                                        onChange={handleDetailPhotoJoignableChange}
                                    >
                                        <FormControlLabel value="true" control={<Radio />} label="Oui" />
                                        <FormControlLabel value="false" control={<Radio />} label="Non" />
                                    </RadioGroup>
                                    <FormHelperText error={detailPhotoJoignableError && detailPhotoJoignableError.errorMsg ? true : false}>{detailPhotoJoignableError ? detailPhotoJoignableError.errorMsg : ''}</FormHelperText>
                                </FormControl>
                            </Stack>
                        </Box>
                    </Paper>
                )}

                {nomBLEqualNomClient === 'true' &&
                    <>
                        <QuestionBooleenne
                            legende="Pouvez-vous indiquer le numéro de colis ?"
                            qualificationDetailCode={ReclamationQualificationDetailCodes.NumeroColisVisible}
                            value={numeroColisVisible}
                            position={1}
                            onChange={handleNumeroColisVisibleChange}
                            required
                    />

                        {renderImageEtiquette()}

                        {numeroColisVisible === 'true' &&

                            <Paper sx={{ mt: 1, p: 1 }}>
                                <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ pl: 1, width: '100%' }}>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => handleImageEtiquetteDialogState(true)}
                                    >
                                        Où trouver ces informations ?
                                    </Button>

                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Numéro de colis commençant par CN ou BA</FormLabel>
                                        <TextField
                                            fullWidth
                                            name="numero-colis-cn-ca"
                                            placeholder="Numéro de colis"
                                            onChange={handleDetailNumeroColisChange}
                                            value={numeroColis}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            error={detailSurColisError && detailSurColisError.errorMsg ? true : false}
                                            helperText={detailSurColisError ? detailSurColisError.errorMsg : ''}
                                            sx={{ minWidth: 560 }}
                                        />
                                    </FormControl>

                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">Numéro de pièce sur le colis</FormLabel>
                                        <TextField
                                            fullWidth
                                            name="numero-piece-sur-colis"
                                            placeholder="Numéro de pièce"
                                            onChange={handleDetailNumeroPieceSurColisChange}
                                            value={numeroPieceSurColis}
                                            InputLabelProps={{
                                                shrink: true
                                            }}
                                            error={detailSurColisError && detailSurColisError.errorMsg ? true : false}
                                            helperText={detailSurColisError ? detailSurColisError.errorMsg : ''}
                                            sx={{ minWidth: 560 }}
                                        />
                                    </FormControl>
                                </Stack>
                            </Paper>
                        }

                        {numeroColisVisible === 'false' && (
                            <Paper sx={{ mt: 1, p: 1 }}>
                                <Box component="fieldset">
                                    <Box component="legend">
                                        <Typography variant="subtitle1">Dans le colis (informations sur le BL)</Typography>
                                    </Box>

                                    <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ pl: 1, width: '100%' }}>
                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">À quel nom est le bon de livraison ?</FormLabel>
                                            <TextField
                                                fullWidth
                                                name="nom-bon-livraison"
                                                placeholder="Nom présent sur le bon de livraison"
                                                onChange={handleDetailNomBonLivraisonChange}
                                                value={nomBonLivraison}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                error={detailColisError && detailColisError.errorMsg ? true : false}
                                                helperText={detailColisError ? detailColisError.errorMsg : ''}
                                                sx={{ minWidth: 560 }}
                                            />
                                        </FormControl>

                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">Pouvez-vous indiquer le numéro de pièce présent dans le colis ?</FormLabel>
                                            <TextField
                                                name="numero-piece-colis"
                                                placeholder="Numéro de pièce"
                                                onChange={handleDetailNumeroPieceColisChange}
                                                value={numeroPieceColis}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                error={detailColisError && detailColisError.errorMsg ? true : false}
                                                helperText={detailColisError ? detailColisError.errorMsg : ''}
                                                sx={{ minWidth: 560 }}
                                            />
                                        </FormControl>

                                        <FormControl component="fieldset">
                                            <FormLabel component="legend">Idéalement, préciser un code article présent dans la commande initiale</FormLabel>
                                            <TextField
                                                fullWidth
                                                name="code-article"
                                                placeholder="Code article"
                                                onChange={handleDetailCodeArticleChange}
                                                value={codeArticle}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                error={detailColisError && detailColisError.errorMsg ? true : false}
                                                helperText={detailColisError ? detailColisError.errorMsg : ''}
                                            />
                                        </FormControl>
                                    </Stack>
                                </Box>
                            </Paper>
                        )}

                    </>
                }


                {((nomBLEqualNomClient === "false" && (nomEtiquette !== "" && photoJoignable !== ""))
                    || (nomBLEqualNomClient === "true" && numeroColisVisible === 'false' && (nomBonLivraison !== "" || numeroPieceColis !== "" || codeArticle !== ""))
                    || (nomBLEqualNomClient === "true" && numeroColisVisible === 'true' && (numeroColis !== "" || numeroPieceSurColis !== ""))) &&
                    (

                        <Paper sx={{ mt: 1, p: 1 }}>
                            <Box component="fieldset">
                                <Box component="legend">
                                    <Typography variant="subtitle1">Informations complémentaires</Typography>
                                </Box>

                                <Stack spacing={1} sx={{ width: '100%' }}>

                                    <FormControl component="fieldset" required sx={{ maxWidth: 560 }}>
                                        <FormLabel component="legend" error={detailDateLivraisonError && detailDateLivraisonError.errorMsg ? true : false}>À quelle date avez vous reçu le colis ?</FormLabel>
                                        <TextField
                                            name="date-reception"
                                            placeholder="Date de réception du colis"
                                            onChange={handleDetailDateLivraisonChange}
                                            value={dateLivraison}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            required
                                            type="date"
                                        />
                                    </FormControl>


                                    <FormControl component="fieldset" required>
                                        <FormLabel component="legend" error={detailAvecLivraisonError && detailAvecLivraisonError.errorMsg ? true : false}>Avec quelle livraison ?</FormLabel>
                                        <RadioGroup
                                            name="LIVRAISON"
                                            sx={{ ml: 2 }}
                                            value={avecLivraison}
                                            onChange={handleDetailAvecLivraisonChange}
                                        >
                                            <FormControlLabel value="0" control={<Radio />} label={"Recherche :" + (avecLivraison === 0 ? numeroPiece : '')} />
                                            <FormControlLabel value="1" control={<Radio />} label="Saisie libre" />
                                            <FormControlLabel value="2" control={<Radio />} label="Pas de livraison attendue." />
                                        </RadioGroup>
                                    </FormControl>

                                    {avecLivraison === 0 && (
                                        <PieceGestionSearch
                                            onSelectPiece={handleSelectPiece}
                                            client={reclamation.client}
                                            withLignes={false}
                                            namespace={namespace}
                                            warningReclamation
                                            defautProduitInfos={false}
                                            logistiqueInfos={false}
                                        />
                                    )}

                                    {avecLivraison === 1 && (
                                        <FormControl component="fieldset" sx={{ maxWidth: 560 }}>
                                            <FormLabel component="legend">Veuillez indiquer le numéro de pièce ?</FormLabel>
                                            <TextField
                                                fullWidth
                                                name="numero-piece"
                                                placeholder="Numéro de pièce"
                                                onChange={handleDetailNumeroPieceChange}
                                                value={numeroPiece}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                sx={{ minWidth: '560px' }}
                                            />
                                        </FormControl>
                                    )}

                                    <FormControl component="fieldset" required>
                                        <FormLabel component="legend" error={detailManqueMarchandiseError && detailManqueMarchandiseError.errorMsg ? true : false}>Manque-t-il de la marchandise ?</FormLabel>
                                        <RadioGroup
                                            name="MANQUE_MARCHANDISE"
                                            sx={{ ml: 2 }}
                                            value={manqueMarchandise}
                                            onChange={handleDetailManqueMarchandiseChange}
                                        >
                                            <FormControlLabel value="false" control={<Radio />} label="Non (il y a un colis en trop)" />
                                            <FormControlLabel value="true" control={<Radio />} label="Oui ( => Formulaire prérempli : Manque un ou plusieurs colis)" />
                                        </RadioGroup>
                                    </FormControl>

                                </Stack>


                            </Box>
                        </Paper>
                    )}
            </>
        );
    };

    return (
        <React.Fragment>
            <TextField
                multiline
                fullWidth
                name="commentaire"
                label="Commentaire"
                placeholder="Décrire le problème rencontré"
                maxRows={3}
                onChange={handleChange}
                value={reclamation.commentaire}
                InputLabelProps={{
                    shrink: true
                }}
                sx={{ mb: 1 }}
            />

            <Box sx={{ mb: 1 }}>
                <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChange} />
            </Box>

            {renderDetailsQualifications()}
        </React.Fragment>
    );
}

export default ColisErreurDestinataireStep;
